import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
  Skeleton,
  Paper,
  Alert,
  Badge,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CardActionArea,
  Rating,
  LinearProgress,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import AlertDialog from "../components/HarvestPipeline/AlertDialog";
import useAlert from "../hooks/useAlert";
import CookieValidationDialog from "../components/CookieValidationDialog";
import CookieWarningBanner from "../components/CookieWarningBanner";
import useCookieValidation from "../hooks/useCookieValidation";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Work as WorkIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  Star as StarIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  BusinessCenter as BusinessCenterIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as EmojiEventsIcon,
  LinkedIn as LinkedInIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { jobBatchesAPI, harvestAPI, candidatesAPI } from "../services/api";
import { fetchJobBatchesFromSupabase } from "../services/supabase";
import axios from "axios";

// Candidate Card Component
const CandidateCard = ({ candidate, onClick, isSelected, onSelect }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return "success";
    if (score >= 60) return "warning";
    return "error";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    return "Fair";
  };

  const handleSelect = (e) => {
    e.stopPropagation();
    onSelect(candidate);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          height: "320px", // Fixed height for all cards
          position: "relative",
          cursor: "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: 4,
          },
        }}
        onClick={() => onClick(candidate)}
      >
        <CardActionArea sx={{ height: "100%" }}>
          <CardContent
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                minHeight: "60px", // Fixed height for header
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
              >
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      whiteSpace: "normal",
                      lineHeight: 1.2,
                      minHeight: "2.4em", // ensures space for 2 lines
                    }}
                  >
                    {candidate.full_name || "Unknown Name"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Checkbox - Top Right Corner */}
            <Box sx={{ position: "absolute", top: 8, right: 8 }}>
              <Checkbox
                checked={isSelected}
                onChange={handleSelect}
                onClick={(e) => e.stopPropagation()}
                size="small"
                color="primary"
              />
            </Box>

            {/* Scores */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Match Score
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {candidate.score}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={candidate.score}
                color={getScoreColor(candidate.score)}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  AI Score
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {candidate.ai_score || 0}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={candidate.ai_score || 0}
                color="info"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            {/* Quick Info - Flexible height */}
            <Box sx={{ mb: 2, flex: 1, minHeight: 0 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.4,
                  height: "2.8em", // Fixed height for 2 lines
                }}
              >
                {candidate.about || "No description available"}
              </Typography>
            </Box>

            {/* Status - Fixed at bottom */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "auto",
              }}
            >
              <Chip
                label={candidate.selected ? "Selected" : "Not Selected"}
                color={candidate.selected ? "success" : "default"}
                size="small"
                variant="outlined"
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                {candidate.message_sent ? (
                  <Chip
                    label="Message Sent"
                    color="success"
                    size="small"
                    variant="filled"
                    icon={<CheckCircleIcon />}
                    sx={{ mb: 0.5 }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No Message
                  </Typography>
                )}
                {candidate.message_sent_at && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {new Date(candidate.message_sent_at).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </motion.div>
  );
};

// Candidate Detail Dialog Component
const CandidateDetailDialog = ({ open, onClose, candidate }) => {
  if (!candidate) return null;

  const parseSkillsField = (skillsString) => {
    if (!skillsString) return [];
    if (Array.isArray(skillsString)) return skillsString;

    try {
      // First try to parse as is (in case it's already valid JSON)
      return JSON.parse(skillsString);
    } catch (error) {
      try {
        // If that fails, try replacing single quotes with double quotes
        const jsonString = skillsString.replace(/'/g, '"');
        return JSON.parse(jsonString);
      } catch (secondError) {
        try {
          // More sophisticated approach for complex nested quotes
          let processedString = skillsString;

          // Handle the case where we have single quotes around property names and values
          // This regex pattern matches: 'property': 'value' and converts to "property": "value"
          processedString = processedString.replace(
            /'([^']*?)':\s*'([^']*?)'/g,
            '"$1": "$2"'
          );

          // Handle any remaining single quotes that might be around property names only
          processedString = processedString.replace(/'([^']*?)':/g, '"$1":');

          // Handle any remaining single quotes around values
          processedString = processedString.replace(
            /:\s*'([^']*?)'/g,
            ': "$1"'
          );

          return JSON.parse(processedString);
        } catch (thirdError) {
          try {
            // Last resort: try a more aggressive approach
            console.warn(
              "Complex JSON parsing failed, attempting aggressive parse:",
              skillsString
            );

            // Replace all single quotes with double quotes, but be more careful
            let cleanedString = skillsString;

            // First, escape any existing double quotes to avoid conflicts
            cleanedString = cleanedString.replace(/"/g, '\\"');

            // Then replace single quotes with double quotes
            cleanedString = cleanedString.replace(/'/g, '"');

            // Finally, unescape the double quotes we escaped earlier
            cleanedString = cleanedString.replace(/\\"/g, '"');

            return JSON.parse(cleanedString);
          } catch (fourthError) {
            console.error(
              "All parsing methods failed for skills field:",
              fourthError
            );
            console.error("Original string:", skillsString);
            return [];
          }
        }
      }
    }
  };

  const parseExperience = (experienceString) => {
    if (!experienceString) return [];
    try {
      return JSON.parse(experienceString);
    } catch (error) {
      console.error("Error parsing experience:", error);
      return [];
    }
  };

  const parseEducation = (educationString) => {
    if (!educationString) return [];
    try {
      return JSON.parse(educationString);
    } catch (error) {
      console.error("Error parsing education:", error);
      return [];
    }
  };

  const parseCertifications = (certificationsString) => {
    if (!certificationsString) return [];
    try {
      return JSON.parse(certificationsString);
    } catch (error) {
      console.error("Error parsing certifications:", error);
      return [];
    }
  };

  const parseCourses = (coursesString) => {
    if (!coursesString) return [];
    try {
      return JSON.parse(coursesString);
    } catch (error) {
      console.error("Error parsing courses:", error);
      return [];
    }
  };

  const parseProjects = (projectsString) => {
    if (!projectsString) return [];
    try {
      return JSON.parse(projectsString);
    } catch (error) {
      console.error("Error parsing projects:", error);
      return [];
    }
  };

  const overlappingSkills = parseSkillsField(candidate.overlapping_skills);
  const absentSkills = parseSkillsField(candidate.absent_skills);
  const experience = parseExperience(candidate.experience);
  const education = parseEducation(candidate.education);
  const certifications = parseCertifications(candidate.certifications);
  const courses = parseCourses(candidate.courses);
  const projects = parseProjects(candidate.projects);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">{candidate.full_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {candidate.headline || "No headline"}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Scores Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Scores & Matching
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="h4"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    {candidate.score}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Match Score
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="h4" color="info.main" fontWeight="bold">
                    {candidate.ai_score || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    AI Score
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* About Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              About
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {candidate.about || "No description available"}
              </Typography>
            </Paper>
          </Grid>

          {/* Match Reasons */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Match Reasons
            </Typography>
            <Paper
              sx={{ p: 2, bgcolor: "success.light", color: "success.dark" }}
            >
              <Typography variant="body2" color="white">
                {candidate.match_reasons || "No match reasons provided"}
              </Typography>
            </Paper>
          </Grid>

          {/* Skills Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Overlapping Skills
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {Array.isArray(overlappingSkills) &&
              overlappingSkills.length > 0 ? (
                overlappingSkills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={`${skill.skill}: ${skill.score}%`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No overlapping skills found
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Missing Skills
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {absentSkills.map((skill, index) => (
                <Chip
                  key={index}
                  label={`${skill.skill}: ${skill.score}%`}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              ))}
              {absentSkills.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No missing skills
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Experience Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Experience
            </Typography>
            {experience.length > 0 ? (
              experience.map((exp, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {exp.title} at {exp.company}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {exp.duration}
                  </Typography>
                  <Typography variant="body2">{exp.description}</Typography>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No experience information available
              </Typography>
            )}
          </Grid>

          {/* Education Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Education
            </Typography>
            {education.length > 0 ? (
              education.map((edu, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {edu.degree}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {edu.school}
                  </Typography>
                  {edu.activities && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {edu.activities}
                    </Typography>
                  )}
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No education information available
              </Typography>
            )}
          </Grid>

          {/* Skills */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Skills
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {candidate.skills || "No skills information available"}
              </Typography>
            </Paper>
          </Grid>

          {/* Projects */}
          {projects.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Projects
              </Typography>
              <Grid container spacing={1}>
                {projects.map((project, index) => (
                  <Grid item xs={6} key={index}>
                    <Chip
                      icon={<AssignmentIcon />}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ lineHeight: 1.2, fontSize: "0.875rem" }}
                          >
                            {project.name.length > 40
                              ? project.name.substring(0, 50) + "..."
                              : project.name}
                          </Typography>
                          {project.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.1, fontSize: "0.8rem" }}
                            >
                              {project.description.length > 35
                                ? project.description.substring(0, 35) + "..."
                                : project.description}
                            </Typography>
                          )}
                        </Box>
                      }
                      color="secondary"
                      variant="outlined"
                      size="medium"
                      sx={{
                        height: "auto",
                        width: "100%",
                        "& .MuiChip-label": {
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          py: 1,
                          width: "100%",
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* Courses */}
          {courses.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Courses
              </Typography>
              <Grid container spacing={1}>
                {courses.map((course, index) => (
                  <Grid item xs={6} key={index}>
                    <Chip
                      icon={<SchoolIcon />}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ lineHeight: 1.2, fontSize: "0.875rem" }}
                          >
                            {course.title.length > 40
                              ? course.title.substring(0, 50) + "..."
                              : course.title}
                          </Typography>
                          {course.associated && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.1, fontSize: "0.8rem" }}
                            >
                              {course.associated.length > 35
                                ? course.associated.substring(0, 35) + "..."
                                : course.associated}
                            </Typography>
                          )}
                        </Box>
                      }
                      color="info"
                      variant="outlined"
                      size="medium"
                      sx={{
                        height: "auto",
                        width: "100%",
                        "& .MuiChip-label": {
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          py: 1,
                          width: "100%",
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* Certifications */}
          {certifications.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Certifications
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {certifications.map((cert, index) => (
                  <Chip
                    key={index}
                    icon={<EmojiEventsIcon />}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {cert.name.length > 35
                            ? cert.name.substring(0, 35) + "..."
                            : cert.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.1 }}
                        >
                          {cert.authority.length > 30
                            ? cert.authority.substring(0, 30) + "..."
                            : cert.authority}
                        </Typography>
                      </Box>
                    }
                    color="primary"
                    variant="outlined"
                    size="medium"
                    sx={{
                      height: "auto",
                      maxWidth: 280,
                      "& .MuiChip-label": {
                        display: "flex",
                        alignItems: "center",
                        px: 1,
                        py: 1,
                      },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          component="a"
          href={candidate.linkedin_url}
          target="_blank"
          startIcon={<LinkedInIcon />}
          variant="outlined"
        >
          View LinkedIn Profile
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// New Job Batch Dialog Component
const NewJobBatchDialog = ({
  open,
  onClose,
  onSubmit,
  jobBatch = null,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    job_description: jobBatch?.job_description || "",
    target_candidates: jobBatch?.target_candidates || 10,
    job_batch_name: jobBatch?.job_batch_name || "",
    expected_salary: jobBatch?.expected_salary || 50000,
    calendly_link: jobBatch?.calendly_link || "",
    mssg_template: jobBatch?.msg_template || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{jobBatch ? "Edit Batch" : "Create New Batch"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Batch Name"
                value={formData.job_batch_name}
                onChange={handleChange("job_batch_name")}
                required
                margin="normal"
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Description"
                value={formData.job_description}
                onChange={handleChange("job_description")}
                required
                multiline
                rows={4}
                margin="normal"
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Target Candidates"
                type="number"
                value={formData.target_candidates}
                onChange={handleChange("target_candidates")}
                required
                margin="normal"
                inputProps={{ min: 1, max: 100 }}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Expected Salary"
                type="number"
                value={formData.expected_salary}
                onChange={handleChange("expected_salary")}
                required
                margin="normal"
                inputProps={{ min: 1 }}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Calendly Link"
                value={formData.calendly_link}
                onChange={handleChange("calendly_link")}
                required
                margin="normal"
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message Template"
                value={formData.mssg_template}
                onChange={handleChange("mssg_template")}
                required
                multiline
                rows={3}
                margin="normal"
                disabled={isLoading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <Skeleton variant="circular" width={20} height={20} />
              ) : null
            }
          >
            {isLoading ? "Processing..." : jobBatch ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const JobBatches = () => {
  const navigate = useNavigate();
  const { showCookieDialog, closeCookieDialog, cookiesExpired, showBanner } =
    useCookieValidation();
  const { alertState, showError, showWarning, showSuccess, hideAlert } =
    useAlert();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const queryClient = useQueryClient();
  const [newJobBatchDialog, setNewJobBatchDialog] = useState(false);
  const [selectedJobBatch, setSelectedJobBatch] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [batchInfo, setBatchInfo] = useState({});
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [candidates, setCandidates] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDetailOpen, setCandidateDetailOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetBatch, setDeleteTargetBatch] = useState(null);
  const [cookies, setCookies] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageType, setMessageType] = useState("without_connection"); // "without_connection" or "with_connection"
  const [message, setMessage] = useState("");
  const [jobBatchesData, setJobBatchesData] = useState([]);
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [batchDetailsLoading, setBatchDetailsLoading] = useState(false);
  const [lastRequestId, setLastRequestId] = useState(null);
  const [messageStatus, setMessageStatus] = useState(null);
  const [checkingMessageStatus, setCheckingMessageStatus] = useState(false);
  const [sentMessages, setSentMessages] = useState({});
  const [stoppingMessage, setStoppingMessage] = useState(false);
  const [sortBy, setSortBy] = useState("ai_score"); // "ai_score", "match_score", "iteration"
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"

  // Helper function to safely parse JSON strings
  const parseSkillsField = (skillsString) => {
    if (!skillsString) return [];
    if (Array.isArray(skillsString)) return skillsString;

    try {
      // First try to parse as is (in case it's already valid JSON)
      return JSON.parse(skillsString);
    } catch (error) {
      try {
        // If that fails, try replacing single quotes with double quotes
        const jsonString = skillsString.replace(/'/g, '"');
        return JSON.parse(jsonString);
      } catch (secondError) {
        try {
          // More sophisticated approach for complex nested quotes
          let processedString = skillsString;

          // Handle the case where we have single quotes around property names and values
          // This regex pattern matches: 'property': 'value' and converts to "property": "value"
          processedString = processedString.replace(
            /'([^']*?)':\s*'([^']*?)'/g,
            '"$1": "$2"'
          );

          // Handle any remaining single quotes that might be around property names only
          processedString = processedString.replace(/'([^']*?)':/g, '"$1":');

          // Handle any remaining single quotes around values
          processedString = processedString.replace(
            /:\s*'([^']*?)'/g,
            ': "$1"'
          );

          return JSON.parse(processedString);
        } catch (thirdError) {
          try {
            // Last resort: try a more aggressive approach
            console.warn(
              "Complex JSON parsing failed, attempting aggressive parse:",
              skillsString
            );

            // Replace all single quotes with double quotes, but be more careful
            let cleanedString = skillsString;

            // First, escape any existing double quotes to avoid conflicts
            cleanedString = cleanedString.replace(/"/g, '\\"');

            // Then replace single quotes with double quotes
            cleanedString = cleanedString.replace(/'/g, '"');

            // Finally, unescape the double quotes we escaped earlier
            cleanedString = cleanedString.replace(/\\"/g, '"');

            return JSON.parse(cleanedString);
          } catch (fourthError) {
            console.error(
              "All JSON parsing attempts failed for skillsString:",
              skillsString
            );
            return [];
          }
        }
      }
    }
  };

  // Sorting function for candidates
  const sortCandidates = (candidatesToSort) => {
    if (!candidatesToSort || candidatesToSort.length === 0)
      return candidatesToSort;

    return [...candidatesToSort].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "ai_score":
          // Handle ai_score which might be null, undefined, or a string
          aValue = typeof a.ai_score === "number" ? a.ai_score : 0;
          bValue = typeof b.ai_score === "number" ? b.ai_score : 0;
          break;
        case "match_score":
          // Handle score which might be null, undefined, or a string
          aValue = typeof a.score === "number" ? a.score : 0;
          bValue = typeof b.score === "number" ? b.score : 0;
          break;
        case "iteration":
          // Handle batch_iteration which might be null, undefined, or a string
          aValue =
            typeof a.batch_iteration === "number" ? a.batch_iteration : 0;
          bValue =
            typeof b.batch_iteration === "number" ? b.batch_iteration : 0;
          break;
        default:
          aValue = typeof a.ai_score === "number" ? a.ai_score : 0;
          bValue = typeof b.ai_score === "number" ? b.ai_score : 0;
      }

      if (sortOrder === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  };

  // Get sorted candidates
  const sortedCandidates = sortCandidates(candidates);

  // Function to fetch complete batch details
  const fetchBatchDetails = async (batchId) => {
    setBatchDetailsLoading(true);
    try {
      const userId = String(localStorage.getItem("userId") || "123");
      const response = await axios.post(
        `http://20.127.236.209:8000/job-batches/get-complete-batch-details/${batchId}?user_id=${userId}`
      );
      setSelectedBatchDetails(response.data.job_batch);
    } catch (error) {
      console.error("Error fetching batch details:", error);
    } finally {
      setBatchDetailsLoading(false);
    }
  };

  // Function to fetch cookies for the selected batch
  const fetchCookies = async (batchId) => {
    try {
      const userId = String(localStorage.getItem("userId") || "123");
      const response = await axios.get(
        `http://20.127.236.209:8000/get-cookies?user_id=${userId}`
      );
      setCookies(response.data);
    } catch (error) {
      console.error("Error fetching cookies:", error);
    }
  };

  useEffect(() => {
    const fetchJobBatches = async () => {
      setIsLoading(true);
      try {
        const userId = String(localStorage.getItem("userId") || "123");
        const response = await fetchJobBatchesFromSupabase(userId);
        setJobBatchesData(response);
      } catch (error) {
        console.error("Error fetching job batches:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobBatches();
  }, []);

  // Auto-check message status when there's a pending request
  useEffect(() => {
    let interval;
    if (lastRequestId && !checkingMessageStatus) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(
            `http://20.127.236.209:8000/candidate-outreaching/send-msg-status?request_id=${lastRequestId}`
          );
          setMessageStatus(response.data);

          // Update sent messages state with the profile URLs that were sent
          if (response.data.messages && response.data.messages.length > 0) {
            const newSentMessages = { ...sentMessages };
            response.data.messages.forEach((msg) => {
              newSentMessages[msg.profile_url] = {
                sent_at: msg.sent_at,
                action_type: msg.action_type,
                batch_id: msg.batch_id,
              };
            });
            setSentMessages(newSentMessages);

            // Update candidates with message sent status
            setCandidates((prevCandidates) =>
              prevCandidates.map((candidate) => {
                if (newSentMessages[candidate.linkedin_url]) {
                  return {
                    ...candidate,
                    message_sent: true,
                    message_sent_at:
                      newSentMessages[candidate.linkedin_url].sent_at,
                  };
                }
                return candidate;
              })
            );
          }

          // Stop auto-checking if messages are processed
          if (response.data.total_processed > 0) {
            setLastRequestId(null);
          }
        } catch (error) {
          console.error("Auto-check message status error:", error);
        }
      }, 10000); // Check every 10 seconds
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [lastRequestId, checkingMessageStatus, sentMessages]);

  const {
    data: candidateData,
    isLoading: candidate_loading,
    refetch,
    error: candidateError,
  } = useQuery(
    ["candidate", page, limit],
    () => candidatesAPI.getByBatch(selectedJobBatch, {}, page),
    {
      refetchInterval: 30000,
      staleTime: 60000,
      enabled: false,
      retry: 2,
      retryDelay: 1000,
    }
  );

  // Mutations
  const createJobBatchMutation = useMutation(
    (jobBatchData) => jobBatchesAPI.create(jobBatchData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["jobBatches"]);
      },
    }
  );

  const updateJobBatchMutation = useMutation(
    (data) => jobBatchesAPI.update(data.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["jobBatches"]);
      },
    }
  );

  const deleteJobBatchMutation = useMutation((id) => jobBatchesAPI.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["jobBatches"]);
    },
    onError: (error) => {
      console.error("Failed to delete job batch:", error);
    },
  });

  // Get loading states from mutations
  const isCreating = createJobBatchMutation.isLoading;
  const isUpdating = updateJobBatchMutation.isLoading;
  const isDeleting = deleteJobBatchMutation.isLoading;

  const handleCellDoubleClick = (title, content) => {
    setDialogTitle(title);
    setDialogContent(content);
    setOpenDialog(true);
  };

  useEffect(() => {
    const fetchSingleCandidateData = async () => {
      if (selectedJobBatch && selectedBatchDetails) {
        setKeywordsLoading(true);
        try {
          const resp = await refetch();
          // Parse skills fields for each candidate
          const parsedCandidates =
            resp.data?.candidates?.map((candidate) => ({
              ...candidate,
              overlapping_skills: parseSkillsField(
                candidate.overlapping_skills
              ),
              absent_skills: parseSkillsField(candidate.absent_skills),
            })) || [];
          setCandidates(parsedCandidates);

          const keywordsRes = await axios.get(
            `http://20.127.236.209:8000/keywords/get-keywords-on-iteration/${selectedJobBatch}`
          );
          setKeywords(keywordsRes?.data);
        } catch (error) {
          console.log(error);
        } finally {
          setKeywordsLoading(false);
        }
      }
    };
    fetchSingleCandidateData();

    // Fetch cookies when batch is selected
    if (selectedJobBatch) {
      fetchCookies(selectedJobBatch);
    }
  }, [selectedJobBatch, selectedBatchDetails]);

  // Handlers
  const handleCreateJobBatch = async (jobBatchData) => {
    try {
      await createJobBatchMutation.mutateAsync(jobBatchData);
    } catch (error) {
      console.error("Failed to create job batch:", error);
    }
  };

  const handleEditJobBatch = (jobBatch) => {
    setSelectedJobBatch(jobBatch);
    setNewJobBatchDialog(true);
  };

  const handleDeleteJobBatch = (id) => {
    setDeleteTargetBatch(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteJobBatch = async () => {
    if (deleteTargetBatch) {
      try {
        await deleteJobBatchMutation.mutateAsync(deleteTargetBatch);

        // Reset selected batch if it was the one being deleted
        if (selectedJobBatch === deleteTargetBatch) {
          setSelectedJobBatch(null);
          setCandidates([]);
          setKeywords([]);
          setSelectedBatchDetails(null);
        }

        // Refresh the job batches dropdown data
        const userId = String(localStorage.getItem("userId") || "123");
        const response = await fetchJobBatchesFromSupabase(userId);
        setJobBatchesData(response);
      } catch (error) {
        console.error("Failed to delete job batch:", error);
      }
    }

    setDeleteDialogOpen(false);
    setDeleteTargetBatch(null);
  };

  const cancelDeleteJobBatch = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetBatch(null);
  };

  const handleStartHarvest = (jobBatch) => {
    navigate("/candidate-sourcing", { state: { jobBatch } });
  };

  const handleViewCandidates = (jobBatchId) => {
    navigate(`/candidates?batch=${jobBatchId}`);
  };

  const handleCandidateClick = (candidate) => {
    setSelectedCandidate(candidate);
    setCandidateDetailOpen(true);
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      // Refresh job batches data
      const userId = String(localStorage.getItem("userId") || "123");
      const response = await fetchJobBatchesFromSupabase(userId);
      setJobBatchesData(response);

      // Refresh candidates if a batch is selected
      if (selectedJobBatch) {
        await refetch();
        // Refresh keywords
        try {
          const keywordsRes = await axios.get(
            `http://20.127.236.209:8000/keywords/get-keywords-on-iteration/${selectedJobBatch}`
          );
          setKeywords(keywordsRes?.data);
        } catch (error) {
          console.log("Error refreshing keywords:", error);
        }

        // Refresh batch details
        if (selectedBatchDetails) {
          await fetchBatchDetails(selectedJobBatch);
        }
      }

      // Invalidate queries
      await queryClient.invalidateQueries(["jobBatches"]);
      await queryClient.invalidateQueries(["candidate"]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Handle candidate selection
  const handleCandidateSelect = (candidate) => {
    if (selectedCandidates.includes(candidate.id)) {
      setSelectedCandidates(
        selectedCandidates.filter((id) => id !== candidate.id)
      );
    } else {
      setSelectedCandidates([...selectedCandidates, candidate.id]);
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    console.log("handleSendMessage called with:", {
      selectedCandidates: selectedCandidates.length,
      cookies: cookies.length,
      messageType,
      message: message.length,
      sendingMessage,
    });

    if (selectedCandidates.length === 0) {
      showWarning(
        "Please select at least one candidate to send a message to.",
        "No Candidates Selected"
      );
      return;
    }

    if (cookies.length === 0) {
      showWarning(
        "No cookies available. Please ensure you have valid LinkedIn cookies configured.",
        "No Cookies Available"
      );
      return;
    }

    // Validate message content
    if (!message.trim()) {
      showWarning("Please enter a message", "Message Required");
      return;
    }

    // Check word count (300 words limit)
    const wordCount = message.trim().split(/\s+/).length;
    if (wordCount > 300) {
      showWarning(
        `Message cannot exceed 300 words. Current word count: ${wordCount}`,
        "Message Too Long"
      );
      return;
    }

    setSendingMessage(true);
    try {
      // Get selected candidates' LinkedIn URLs and names in the same order
      const selectedCandidateData = candidates.filter((c) =>
        selectedCandidates.includes(c.id)
      );

      // Create profiles object with name as key and url as value
      const profiles = {};
      selectedCandidateData
        .filter((candidate) => candidate.linkedin_url)
        .forEach((candidate) => {
          profiles[candidate.full_name || "Unknown"] = candidate.linkedin_url;
        });

      // Get cookies data
      const cookieData = cookies[0]; // Using first cookie entry

      const userId = String(localStorage.getItem("userId") || "123");
      const messageData = {
        user_id: userId,
        batch_id: selectedJobBatch,
        cookies_value: cookieData.cookies_value,
        user_agent: cookieData.user_agent,
        profiles: profiles,
        msg: message,
      };

      console.log("Sending message with data:", messageData);

      // Send message
      const response = await axios.post(
        "http://20.127.236.209:8000/candidate-outreaching/send-msg",
        messageData
      );

      console.log("Message sent successfully:", response.data);
      setLastRequestId(response.data.request_id);
      setMessageStatus(null); // Clear previous status
      setCheckingMessageStatus(false);

      // Close modal and reset
      setMessageModalOpen(false);
      setMessage("");
      setSelectedCandidates([]);

      // Show success message with request ID
    } catch (error) {
      console.error("Error sending messages:", error);
      showError(
        "Failed to send messages. Please try again.",
        "Send Message Failed"
      );
    } finally {
      setSendingMessage(false);
    }
  };

  // Function to check message status
  const checkMessageStatus = async () => {
    if (!lastRequestId) {
      showWarning("No message request ID to check.", "No Request ID");
      return;
    }
    setCheckingMessageStatus(true);
    try {
      const userId = String(localStorage.getItem("userId") || "123");
      const response = await axios.get(
        `http://20.127.236.209:8000/candidate-outreaching/send-msg-status?request_id=${lastRequestId}`
      );
      setMessageStatus(response.data);

      // Update sent messages state with the profile URLs that were sent
      if (response.data.messages && response.data.messages.length > 0) {
        const newSentMessages = { ...sentMessages };
        response.data.messages.forEach((msg) => {
          newSentMessages[msg.profile_url] = {
            sent_at: msg.sent_at,
            action_type: msg.action_type,
            batch_id: msg.batch_id,
          };
        });
        setSentMessages(newSentMessages);

        // Update candidates with message sent status
        setCandidates((prevCandidates) =>
          prevCandidates.map((candidate) => {
            if (newSentMessages[candidate.linkedin_url]) {
              return {
                ...candidate,
                message_sent: true,
                message_sent_at:
                  newSentMessages[candidate.linkedin_url].sent_at,
              };
            }
            return candidate;
          })
        );
      }
    } catch (error) {
      console.error("Error checking message status:", error);
      showError(
        "Failed to check message status. Please try again.",
        "Status Check Failed"
      );
    } finally {
      setCheckingMessageStatus(false);
    }
  };

  // Function to stop message sending
  const stopMessageSending = async () => {
    if (!lastRequestId) {
      showWarning("No message request ID to stop.", "No Request ID");
      return;
    }
    setStoppingMessage(true);
    try {
      const response = await axios.get(
        `http://20.127.236.209:8000/candidate-outreaching/stop-msg?request_id=${lastRequestId}`
      );
      console.log("Message sending stopped:", response.data);
      showSuccess(
        "Message sending has been stopped successfully.",
        "Message Stopped"
      );
      setLastRequestId(null);
      setMessageStatus(null);
    } catch (error) {
      console.error("Error stopping message sending:", error);
      showError(
        "Failed to stop message sending. Please try again.",
        "Stop Failed"
      );
    } finally {
      setStoppingMessage(false);
    }
  };

  // Get selected batch's message template
  const getSelectedBatchMessageTemplate = () => {
    return selectedBatchDetails?.msg_template || "";
  };

  // Handle opening message modal with default template
  const handleOpenMessageModal = () => {
    const defaultMessage = getSelectedBatchMessageTemplate();
    setMessage(defaultMessage);
    setMessageModalOpen(true);
  };

  // Extract data from response
  const jobBatches = jobBatchesData?.job_batches || [];
  const pagination = jobBatchesData?.pagination;

  // Calculate stats
  const totalBatches = pagination?.total || 0;
  const activeBatches = jobBatches.filter((b) => b.status === "active").length;
  const totalCandidates = jobBatches.reduce(
    (sum, b) => sum + (b.candidates_count || b.actual_candidates || 0),
    0
  );
  const avgScore =
    jobBatches.length > 0
      ? Math.round(
          jobBatches.reduce((sum, b) => sum + (b.avg_score || 0), 0) /
            jobBatches.length
        )
      : 0;

  // Show main loading state
  if (isLoading) {
    return (
      <Box>
        {/* Header Skeleton */}
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>

        {/* Select Dropdown Skeleton */}
        <Skeleton variant="text" width="20%" height={24} sx={{ mb: 1 }} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={56}
          sx={{ mb: 3 }}
        />

        {/* Content Skeleton */}
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Cookie Warning Banner */}
      <CookieWarningBanner cookiesExpired={showBanner} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Browse Batches
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your recruitment campaigns and job postings
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={refreshData}
              color="primary"
              disabled={isRefreshing}
            >
              <RefreshIcon
                sx={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
          {/* <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNewJobBatchDialog(true)}
            disabled={isCreating || isUpdating || isDeleting}
          >
            New Batch
          </Button> */}
        </Box>
      </Box>

      {/* Batches Grid */}
      <Typography variant="body2">Select Batch</Typography>
      <FormControl fullWidth sx={{ mt: 1 }}>
        {!selectedJobBatch ? <InputLabel> --Please Select-- </InputLabel> : ""}

        <Select
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 250,
              },
            },
          }}
          label={!selectedJobBatch ? "--Please Select--" : ""}
          value={selectedJobBatch}
          onChange={(e) => {
            const batchId = e.target.value;
            setSelectedJobBatch(batchId);
            // Clear selected candidates when changing batches
            setSelectedCandidates([]);
            if (batchId) {
              fetchBatchDetails(batchId);
            } else {
              setSelectedBatchDetails(null);
            }
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <MenuItem disabled>
              <Skeleton variant="text" width="100%" height={24} />
            </MenuItem>
          ) : (
            jobBatchesData?.job_batches
              ?.filter((batch) => batch?.job_batch_name !== null)
              ?.map((batch) => (
                <MenuItem key={batch.id} value={batch?.id}>
                  {batch?.job_batch_name}
                </MenuItem>
              ))
          )}
        </Select>
      </FormControl>

      {selectedJobBatch && (selectedBatchDetails || batchDetailsLoading) ? (
        <>
          {batchDetailsLoading ? (
            <Box mt={4} mb={2}>
              <Skeleton variant="text" width="30%" height={48} />
            </Box>
          ) : (
            <Box
              mt={4}
              mb={2}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 1,
                  mb: 1,
                }}
              >
                Batch #{selectedJobBatch}
              </Typography>
              {!batchDetailsLoading && selectedBatchDetails?.status && (
                <Chip
                  label={selectedBatchDetails.status}
                  size="medium"
                  color={
                    selectedBatchDetails.status === "Active" ||
                    selectedBatchDetails.status === "Running"
                      ? "success"
                      : selectedBatchDetails.status === "Stopped" ||
                        selectedBatchDetails.status === "Paused"
                      ? "warning"
                      : selectedBatchDetails.status === "Completed"
                      ? "info"
                      : "default"
                  }
                  variant="filled"
                  sx={{
                    fontSize: "0.875rem",
                    height: "32px",
                    fontWeight: 600,
                    "& .MuiChip-label": {
                      px: 2,
                    },
                  }}
                />
              )}
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                sx={{ height: 48 }}
                onClick={() => handleDeleteJobBatch(selectedJobBatch)}
                disabled={isDeleting}
              >
                Delete Batch
              </Button>
            </Box>
          )}

          <Accordion sx={{ mt: 2, boxShadow: 1, borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Job Description</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {batchDetailsLoading ? (
                <Skeleton variant="text" width="100%" height={60} />
              ) : (
                <Typography variant="body2">
                  {selectedBatchDetails?.job_description ||
                    "No description available"}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2, boxShadow: 1, borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>
                Keywords on Each Iteration
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {keywords?.map((keyword, index) => (
                <Typography variant="body2" key={index} sx={{ mb: 1 }}>
                  <Typography component="span" fontWeight="bold">
                    Batch iteration {keyword?.batch_iteration || "N/A"}
                  </Typography>{" "}
                  — Iteration {keyword?.iteration} — {keyword?.keywords}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>

          <Grid container spacing={4} sx={{ mt: 3, mb: 3, width: "60%" }}>
            <Grid item xs={6}>
              <Paper
                elevation={2}
                sx={{ p: 2, textAlign: "center", position: "relative" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Target Candidates
                </Typography>
                {batchDetailsLoading ? (
                  <Skeleton variant="text" width="60%" height={40} />
                ) : (
                  <Typography variant="h5" fontWeight={700}>
                    {selectedBatchDetails?.target_candidates || 0}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper elevation={2} sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Actual Candidates
                </Typography>
                {batchDetailsLoading ? (
                  <Skeleton variant="text" width="60%" height={40} />
                ) : (
                  <Typography variant="h5" fontWeight={700}>
                    {selectedBatchDetails?.actual_candidates || 0}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {candidate_loading || keywordsLoading || batchDetailsLoading ? (
            <Box mt={3}>
              {/* Job Description Skeleton */}
              <Skeleton
                variant="rectangular"
                width="100%"
                height={60}
                sx={{ mb: 2 }}
              />

              {/* Keywords Skeleton */}
              <Skeleton
                variant="rectangular"
                width="100%"
                height={60}
                sx={{ mb: 2 }}
              />

              {/* Stats Skeleton */}
              <Box sx={{ display: "flex", gap: "100px", width: "50%", mb: 3 }}>
                <Box>
                  <Skeleton variant="text" width={120} height={24} />
                  <Skeleton variant="text" width={80} height={32} />
                </Box>
                <Box>
                  <Skeleton variant="text" width={120} height={24} />
                  <Skeleton variant="text" width={80} height={32} />
                </Box>
              </Box>

              {/* Cards Skeleton */}
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={200}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <>
              {/* Header Row with Title and Send Message Button */}
              <Box
                mt={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h4">
                    Candidates for Batch #{selectedJobBatch}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Click on any candidate card to view detailed information
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  {lastRequestId && (
                    <>
                      <Button
                        variant="outlined"
                        color="info"
                        size="medium"
                        onClick={checkMessageStatus}
                        disabled={checkingMessageStatus}
                        startIcon={<RefreshIcon />}
                        sx={{ px: 3, py: 1 }}
                      >
                        {checkingMessageStatus
                          ? "Checking..."
                          : "Check Message Status"}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="medium"
                        onClick={stopMessageSending}
                        disabled={stoppingMessage}
                        startIcon={<DeleteIcon />}
                        sx={{ px: 3, py: 1 }}
                      >
                        {stoppingMessage ? "Stopping..." : "Stop Message"}
                      </Button>
                    </>
                  )}
                  {selectedCandidates.length > 0 && (
                    <Tooltip
                      title={
                        cookiesExpired
                          ? "Add cookies to continue"
                          : "Send message to selected candidates"
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={handleOpenMessageModal}
                          disabled={sendingMessage || cookiesExpired}
                          sx={{ px: 4, py: 1.5 }}
                        >
                          Send Message
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* Selection Counter and Message Status */}
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                {selectedCandidates.length > 0 && (
                  <Chip
                    label={`${selectedCandidates.length} candidate${
                      selectedCandidates.length > 1 ? "s" : ""
                    } selected`}
                    color="primary"
                    variant="filled"
                    size="medium"
                  />
                )}
                {messageStatus && (
                  <Chip
                    label={`${
                      messageStatus.total_processed || 0
                    } message(s) processed`}
                    color="success"
                    variant="outlined"
                    size="medium"
                    icon={<CheckCircleIcon />}
                  />
                )}
              </Box>

              {/* Sorting Controls */}
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Sort by:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    displayEmpty
                    startAdornment={
                      <SortIcon sx={{ mr: 1, color: "text.secondary" }} />
                    }
                  >
                    <MenuItem value="ai_score">AI Score</MenuItem>
                    <MenuItem value="match_score">Match Score</MenuItem>
                    <MenuItem value="iteration">Batch Iteration</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="desc">Descending</MenuItem>
                    <MenuItem value="asc">Ascending</MenuItem>
                  </Select>
                </FormControl>
                <Chip
                  label={`${sortedCandidates.length} candidates`}
                  color="info"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Sorted by ${
                    sortBy === "ai_score"
                      ? "AI Score"
                      : sortBy === "match_score"
                      ? "Match Score"
                      : "Batch Iteration"
                  } (${sortOrder === "desc" ? "High to Low" : "Low to High"})`}
                  color="primary"
                  variant="filled"
                  size="small"
                  sx={{ fontSize: "0.75rem" }}
                />
              </Box>

              {/* Error Display */}
              {candidateError && (
                <Box mt={2}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Failed to load candidates. Please try refreshing the page.
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Error:{" "}
                      {candidateError.message || "Unknown error occurred"}
                    </Typography>
                  </Alert>
                </Box>
              )}

              {/* Candidates Grid */}
              <Box mt={3}>
                <Grid container spacing={3}>
                  {sortedCandidates?.map((candidate, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={candidate.id || index}
                    >
                      <CandidateCard
                        candidate={candidate}
                        onClick={handleCandidateClick}
                        isSelected={selectedCandidates.includes(candidate.id)}
                        onSelect={handleCandidateSelect}
                      />
                    </Grid>
                  ))}
                </Grid>

                {sortedCandidates?.length === 0 && !candidateError && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No candidates found for this batch
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </>
      ) : (
        ""
      )}

      {/* Candidate Detail Dialog */}
      <CandidateDetailDialog
        open={candidateDetailOpen}
        onClose={() => {
          setCandidateDetailOpen(false);
          setSelectedCandidate(null);
        }}
        candidate={selectedCandidate}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          <div style={{ whiteSpace: "pre-wrap" }}>{dialogContent}</div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* New Job Batch Dialog */}
      <NewJobBatchDialog
        open={newJobBatchDialog}
        onClose={() => {
          setNewJobBatchDialog(false);
          setSelectedJobBatch(null);
        }}
        onSubmit={
          selectedJobBatch
            ? (data) =>
                updateJobBatchMutation.mutate({
                  ...data,
                  id: selectedJobBatch.id,
                })
            : handleCreateJobBatch
        }
        jobBatch={selectedJobBatch}
        isLoading={isCreating || isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={cancelDeleteJobBatch}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this job batch? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteJobBatch} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteJobBatch}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Message Modal */}
      <Dialog
        open={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Send Message to {selectedCandidates.length} Selected Candidate
          {selectedCandidates.length > 1 ? "s" : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Selected Candidates:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {candidates
                .filter((c) => selectedCandidates.includes(c.id))
                .map((candidate, index) => (
                  <Chip
                    key={candidate.id}
                    label={candidate.full_name}
                    size="small"
                    variant="outlined"
                  />
                ))}
            </Box>
          </Box>

          {/* Calendly Link Warning */}
          {(!selectedBatchDetails?.calendly_link ||
            selectedBatchDetails?.calendly_link.trim() === "") && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  Calendly link is not available
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Message Input Field */}
          <TextField
            fullWidth
            label="Message"
            multiline
            rows={6}
            value={message}
            onChange={(e) => {
              const value = e.target.value;
              setMessage(value);
            }}
            placeholder="Enter your message for candidates..."
            disabled={sendingMessage}
            helperText={`${message.trim().split(/\s+/).length}/300 words`}
            sx={{ mt: 2 }}
          />

          {lastRequestId && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Message Status:
              </Typography>
              {checkingMessageStatus ? (
                <Skeleton variant="text" width="100%" height={24} />
              ) : messageStatus ? (
                <Typography variant="body2">
                  {messageStatus.status === "completed"
                    ? "Message sent successfully!"
                    : messageStatus.status === "failed"
                    ? "Message failed to send."
                    : `Message status: ${messageStatus.status}`}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No status available for this message.
                </Typography>
              )}
              {lastRequestId && (
                <Button
                  variant="outlined"
                  onClick={checkMessageStatus}
                  sx={{ mt: 1 }}
                  disabled={checkingMessageStatus}
                >
                  {checkingMessageStatus ? "Checking..." : "Check Status"}
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMessageModalOpen(false)}
            disabled={sendingMessage}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            disabled={!message.trim() || sendingMessage}
            startIcon={
              sendingMessage ? (
                <Skeleton variant="circular" width={20} height={20} />
              ) : null
            }
            title={
              !message.trim()
                ? "Please enter a message"
                : sendingMessage
                ? "Message is being sent..."
                : "Send message to selected candidates"
            }
          >
            {sendingMessage ? "Sending..." : "Send Message"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cookie Validation Dialog */}
      <CookieValidationDialog
        open={showCookieDialog}
        onClose={closeCookieDialog}
      />

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        severity={alertState.severity}
        confirmText={alertState.confirmText}
        showCancel={alertState.showCancel}
        cancelText={alertState.cancelText}
        onConfirm={alertState.onConfirm}
      />
    </Box>
  );
};

export default JobBatches;
